// src/pages/Shops.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Range } from 'react-range';
import { useDispatch, useSelector } from 'react-redux';

import ShopsHeader from '../components/ShopsHeader';
import Footer from '../components/Footer';
import ShopProducts from '../components/products/ShopProducts';
import shopgif from '../assets/banner/shop.gif';

import {
  price_range_product,
  query_products,
  getProductsBySeller,
  fetchShops
} from '../store/reducers/homeReducer';

import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import {
  AiFillStar,
  AiOutlineSortAscending,
  AiOutlineClose
} from 'react-icons/ai';
import { CiStar } from 'react-icons/ci';
import { BsFillGridFill } from 'react-icons/bs';
import { FaThList, FaFilter, FaVenusMars } from 'react-icons/fa';

import { useEnrichProducts } from '../components/products/enrichedProducts';

const FILTER_SECTIONS = [
  'Categories',
  'Shops',
  'Price',
  'Rating'
];

const Shops = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const category = searchParams.getAll('category');
  const sellerParam = searchParams.getAll('seller');
  const ratingParam = searchParams.get('rating') || '';
  const searchValue = searchParams.get('search') || '';
  const sortQuery = searchParams.get('sort') || '';
  const urlLow = searchParams.get('low');
  const urlHigh = searchParams.get('high');

  // ── LOADER STATE & CONFIG ─────────────────────────────────────
  const loaderList = useMemo(
    () => [
      '/images/Loader1.mp4',
      '/images/Loader2.mp4',
      '/images/Loader3.mp4'
    ],
    []
  );
  const [loaderSrc] = useState(
    () => loaderList[Math.floor(Math.random() * loaderList.length)]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  const MIN_LOADER_TIME_MS = 3000;
  const LOADER_WIDTH = '150px';
  const LOADER_HEIGHT = '200px';
  const LOADER_BORDER_RADIUS = '50px';
  const VIDEO_PLAYBACK_RATE = 2.2;
  // ────────────────────────────────────────────────────────────────

  // block scroll while loader is active
  useEffect(() => {
    document.body.style.overflow = isLoading ? 'hidden' : 'auto';
  }, [isLoading]);

  // enforce minimum loader display time
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), MIN_LOADER_TIME_MS);
    return () => clearTimeout(timer);
  }, []);

  // fetch price-range & shops once
  useEffect(() => {
    dispatch(price_range_product());
    dispatch(fetchShops());
  }, [dispatch]);

  const { priceRange, categorys, shops } = useSelector(s => s.home);
  const storeLow = priceRange?.low ?? 0;
  const storeHigh = priceRange?.high ?? 999999;

  const [isFiltering, setIsFiltering] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeSection, setActiveSection] = useState('Categories');
  const [showSortMenu, setShowSortMenu] = useState(false);

  // temp-states for multi-select
  const [tempCategories, setTempCategories] = useState([]);
  const [tempShops, setTempShops] = useState([]);
  const [tempRating, setTempRating] = useState(null);

  const [fetchedProducts, setFetchedProducts] = useState([]);
  const [shownProducts, setShownProducts] = useState([]);

  const [sliderMin, setSliderMin] = useState(0);
  const [sliderMax, setSliderMax] = useState(10000);
  const [tempSliderValues, setTempSliderValues] = useState([0, 10000]);

  // sync slider bounds from store
  useEffect(() => {
    if (priceRange.low == null) return;
    setSliderMin(priceRange.low);
    setSliderMax(priceRange.high);
    setTempSliderValues([priceRange.low, priceRange.high]);
  }, [priceRange]);

  // ─── fetch products again on ANY change to the query string ───
  useEffect(() => {
    if (priceRange.low == null) return;

    (async () => {
      setIsFiltering(true);
      const start = Date.now();

      // re-read all filters directly from searchParams
      const cats = searchParams.getAll('category');
      const sellers = searchParams.getAll('seller');
      const ratingParam = searchParams.get('rating') || '';
      const searchParam = searchParams.get('search') || '';

      const sortParam = searchParams.get('sort') || '';
      let lowVal = Number(searchParams.get('low') ?? storeLow);
      let highVal = Number(searchParams.get('high') ?? storeHigh);
      if (lowVal > highVal) [lowVal, highVal] = [highVal, lowVal];

      const base = {
        low: lowVal,
        high: highVal,
        category: cats.join(','),
        sellerId: sellers.join(','),
        rating: ratingParam,
        sortPrice: sortParam,
        searchValue: searchParam,
        pageNumber: 1
      };

      try {
        let result;
        if (sellers.length) {
          result = await dispatch(getProductsBySeller(base)).unwrap();
        } else {
          result = await dispatch(query_products(base)).unwrap();
        }
        setFetchedProducts(result.products);
      } catch (e) {
        console.error('Error loading shop products', e);
      } finally {
        setIsDataLoaded(true);
        const wait = 1000 - (Date.now() - start);
        setTimeout(() => setIsFiltering(false), wait > 0 ? wait : 0);
      }
    })();
  }, [dispatch, location.search, priceRange.low, priceRange.high]);

  // hide loader once both data + time are ready
  useEffect(() => {
    if (isDataLoaded && minTimeElapsed) {
      setIsLoading(false);
    }
  }, [isDataLoaded, minTimeElapsed]);

  // infinite scroll into shownProducts
  useEffect(() => {
    if (!isFiltering) setShownProducts(fetchedProducts.slice(0, 30));
  }, [fetchedProducts, isFiltering]);
  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 100
      ) {
        setShownProducts(prev => {
          const next = fetchedProducts.slice(prev.length, prev.length + 30);
          return next.length ? [...prev, ...next] : prev;
        });
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [fetchedProducts]);

  const enrichedProducts = useEnrichProducts(shownProducts);

  // sync modal temp-filters from URL on open
  useEffect(() => {
    if (!showFilterModal) return;
    setTempCategories(searchParams.getAll('category'));
    setTempShops(searchParams.getAll('seller'));
    setTempRating(ratingParam ? Number(ratingParam) : null);
    setTempSliderValues([
      searchParams.get('low') ? Number(searchParams.get('low')) : storeLow,
      searchParams.get('high') ? Number(searchParams.get('high')) : storeHigh
    ]);
  }, [showFilterModal]);

  const applyFilters = () => {
    const np = new URLSearchParams();
    if (searchParams.get('search')) np.set('search', searchParams.get('search'));
    if (searchParams.get('sort')) np.set('sort', searchParams.get('sort'));

    tempCategories.forEach(c => np.append('category', c));
    tempShops.forEach(s => np.append('seller', s));
    if (tempRating) np.set('rating', String(tempRating));
    np.set('low', String(tempSliderValues[0]));
    np.set('high', String(tempSliderValues[1]));

    setSearchParams(np);
    setShowFilterModal(false);
  };

  const [styles, setStyles] = useState('grid');

  // ── LOADER UI ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div
          style={{
            width: LOADER_WIDTH,
            height: LOADER_HEIGHT,
            overflow: 'hidden',
            borderRadius: LOADER_BORDER_RADIUS
          }}
        >
          <video
            autoPlay
            loop
            muted
            preload="none"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onLoadedMetadata={e => { e.currentTarget.playbackRate = VIDEO_PLAYBACK_RATE; }}
          >
            <source src={loaderSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    );
  }

  // ── MAIN UI ─────────────────────────────────────────────────
  const headerTitle = searchParams.get('search') || 'Total';
  const headerCount = fetchedProducts.length;

  return (
    <div className="relative min-h-screen flex flex-col">
      {/* 1) Header */}
      <ShopsHeader title={headerTitle} count={headerCount}/>

      {/* 2) Banner */}
      <section className="h-[200px] w-full mt-6 relative overflow-hidden">
        <img
          src={shopgif}
          alt="Shop Banner"
          className="w-full h-full object-cover scale-110 opacity-90"
        />
        <div className="absolute inset-0 bg-[#2422228a] flex items-center justify-center">
          <div className="text-white flex flex-col gap-1 items-center">
            <h2 className="text-2xl font-bold tracking-wide">Shop.my</h2>
            <div className="flex items-center gap-2 text-xl">
              <Link to="/" className="hover:text-yellow-300 transition-colors text-white">
                Home
              </Link>
              <MdOutlineKeyboardArrowRight />
              <span>Products</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3) Products */}
      <section
        className="pt-7 pb-16 relative w-full"
        style={{ background: 'linear-gradient(to bottom, #f3efe9, #d9d0c3)' }}
      >
        <div className="mx-auto w-[95%] z-10">
          <div className="w-full flex flex-wrap p-2">
            <div className="w-full">
              {!isFiltering ? (
                <div className="flex justify-end mb-4 px-3">
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                    <button
                      onClick={() => setStyles('grid')}
                      className={`flex flex-col items-center px-4 py-2 focus:outline-none ${styles === 'grid' ? 'bg-teal-100' : 'bg-white'
                        }`}
                    >
                      <BsFillGridFill className="text-xl" />
                      <span className="text-xs mt-1">Grid</span>
                    </button>
                    <div className="w-px h-6 bg-gray-300" />
                    <button
                      onClick={() => setStyles('list')}
                      className={`flex flex-col items-center px-4 py-2 focus:outline-none ${styles === 'list' ? 'bg-teal-100' : 'bg-white'
                        }`}
                    >
                      <FaThList className="text-xl" />
                      <span className="text-xs mt-1">List</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full h-screen flex items-center justify-center">
                  <div
                    style={{
                      width: LOADER_WIDTH,
                      height: LOADER_HEIGHT,
                      overflow: 'hidden',
                      borderRadius: LOADER_BORDER_RADIUS,
                      
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <video
                      autoPlay
                      loop
                      muted
                      preload="none"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onLoadedMetadata={e => {
                        e.currentTarget.playbackRate = VIDEO_PLAYBACK_RATE;
                      }}
                    >
                      <source src={loaderSrc} type="video/mp4" />
                    </video>
                  </div>
                </div>
              )}
              <div className="pb-10">
                <ShopProducts products={enrichedProducts} styles={styles} />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* 4) Footer */}
      <Footer />

      {/* 5) Bottom bar */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t shadow-md flex justify-around items-center py-2 z-50">
        <button
          onClick={() => { setActiveSection('Categories'); setShowFilterModal(true); }}
          className="flex flex-col items-center text-gray-600"
        >
          <FaVenusMars className="text-xl" />
          <span className="text-xs">Categories</span>
        </button>
        <button
          onClick={() => setShowSortMenu(v => !v)}
          className="flex flex-col items-center text-gray-600"
        >
          <AiOutlineSortAscending className="text-xl" />
          <span className="text-xs">Sort</span>
        </button>
        <button
          onClick={() => { setActiveSection('Categories'); setShowFilterModal(true); }}
          className="flex flex-col items-center text-gray-600"
        >
          <FaFilter className="text-xl" />
          <span className="text-xs">Filter</span>
        </button>
      </div>

      {/* Sort Popup */}
      {showSortMenu && (
        <div className="fixed bottom-16 left-1/2 transform -translate-x-1/2 bg-white shadow-md rounded-md py-2 px-4 z-50">
          <button
            className={`${searchParams.get('sort') === 'low-to-high' ? 'font-semibold' : ''} block w-full text-left py-1`}
            onClick={() => setSearchParams(q => { const np = new URLSearchParams(q); np.set('sort', 'low-to-high'); return np; })}
          >
            Low → High Price
          </button>
          <button
            className={`${searchParams.get('sort') === 'high-to-low' ? 'font-semibold' : ''} block w-full text-left py-1`}
            onClick={() => setSearchParams(q => { const np = new URLSearchParams(q); np.set('sort', 'high-to-low'); return np; })}
          >
            High → Low Price
          </button>
        </div>
      )}

      {/* Pro Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-white z-50 flex">
          {/* Left nav */}
          <div className="w-1/3 border-r overflow-auto">
            <div className="p-4 flex justify-between items-center border-b">
              <span className="text-xl font-bold">Filters</span>
              <button onClick={() => setShowFilterModal(false)} className="text-2xl">
                <AiOutlineClose />
              </button>
            </div>
            <button
              onClick={() => { setSearchParams(new URLSearchParams()); window.location.reload(); }}
              className="text-sm text-pink-600 px-4 py-2"
            >
              CLEAR ALL
            </button>
            <nav className="mt-4">
              {FILTER_SECTIONS.map(sec => (
                <button
                  key={sec}
                  onClick={() => setActiveSection(sec)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-100 ${activeSection === sec ? 'bg-gray-200 font-semibold' : ''}`}
                >
                  {sec}
                </button>
              ))}
            </nav>
          </div>

          {/* Right pane */}
          <div className="w-2/3 p-6 overflow-auto">
            {activeSection === 'Categories' && (
              <div className="grid grid-cols-2 gap-4">
                {categorys?.map((c, i) => (
                  <label key={i} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={tempCategories.includes(c.name)}
                      onChange={e => e.target.checked ? setTempCategories(prev => [...prev, c.name]) : setTempCategories(prev => prev.filter(x => x !== c.name))}
                      className="w-4 h-4 accent-teal-600"
                    />
                    <span className="text-slate-700">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
            {activeSection === 'Shops' && (
              <div className="grid grid-cols-2 gap-4">
                {shops?.map((s, i) => (
                  <label key={i} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={tempShops.includes(s.sellerId)}
                      onChange={e => e.target.checked ? setTempShops(prev => [...prev, s.sellerId]) : setTempShops(prev => prev.filter(x => x !== s.sellerId))}
                      className="w-4 h-4 accent-teal-600"
                    />
                    <span className="text-slate-700">{s.shopName}</span>
                  </label>
                ))}
              </div>
            )}
            {activeSection === 'Price' && (
              <div>
                <Range
                  step={1}
                  min={sliderMin}
                  max={sliderMax}
                  values={tempSliderValues}
                  onChange={setTempSliderValues}
                  renderTrack={({ props, children }) => (
                    <div {...props} className="h-1 bg-gray-300 rounded">{children}</div>
                  )}
                  renderThumb={({ props }) => (
                    <div {...props} className="w-4 h-4 bg-teal-600 rounded-full" />
                  )}
                />
                <div className="mt-2 text-gray-700">
                  ₹{tempSliderValues[0]} – ₹{tempSliderValues[1]}
                </div>
                <button onClick={applyFilters} className="mt-4 px-4 py-2 bg-pink-600 text-white rounded">
                  APPLY
                </button>
              </div>
            )}
            {activeSection === 'Rating' && (
              <div className="space-y-3 text-xl">
                {[5, 4, 3, 2, 1].map(r => (
                  <div
                    key={r}
                    className={`flex items-center gap-2 p-2 border rounded hover:bg-gray-50 ${tempRating === r ? 'bg-teal-100' : ''}`}
                    onClick={() => setTempRating(r)}
                  >
                    {Array.from({ length: r }).map((_, i) => <AiFillStar key={i} />)}
                    {Array.from({ length: 5 - r }).map((_, i) => <CiStar key={i} />)}
                  </div>
                ))}
                <button onClick={() => setTempRating(null)} className="text-sm text-gray-600 mt-2">
                  Reset
                </button>
              </div>
            )}
          </div>

          {/* Footer APPLY/CLOSE */}
          <div className="absolute bottom-0 w-full flex border-t bg-white">
            <button onClick={() => setShowFilterModal(false)} className="flex-1 py-3 text-center">
              CLOSE
            </button>
            <button onClick={applyFilters} className="flex-1 py-3 text-center bg-pink-600 text-white">
              APPLY
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shops;
